import { useState } from "react";
import api from "@/lib/api";
import { Upload, X, FileText, Image as ImgIcon } from "lucide-react";
import { toast } from "sonner";

/**
 * value = [{id, original_filename, content_type}]
 * onChange = (next) => void
 */
export default function FileUpload({ value = [], onChange, accept = "image/*,application/pdf", label, testid }) {
  const [busy, setBusy] = useState(false);

  const upload = async (files) => {
    setBusy(true);
    try {
      const next = [...value];
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        next.push(data);
      }
      onChange(next);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = (id) => onChange(value.filter((f) => f.id !== id));

  return (
    <div data-testid={testid}>
      {label && <label className="field-label">{label}</label>}
      <label
        className="flex items-center justify-center gap-2 cursor-pointer py-6 rounded-2xl transition"
        style={{
          background: "var(--surface2)",
          border: "1px dashed var(--border)",
          color: "var(--text-muted)",
        }}
      >
        <input
          type="file"
          multiple
          accept={accept}
          className="hidden"
          onChange={(e) => e.target.files && upload(e.target.files)}
          data-testid={`${testid}-input`}
        />
        {busy ? <span className="spinner" /> : <Upload size={16} />}
        <span className="text-sm">{busy ? "Uploading..." : "Click to upload (or drag)"}</span>
      </label>
      {value.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
          {value.map((f) => {
            const isImg = f.content_type?.startsWith("image/");
            return (
              <div
                key={f.id}
                className="relative rounded-2xl overflow-hidden p-3 flex items-center gap-2"
                style={{ background: "var(--surface2)", border: "1px solid var(--border)" }}
              >
                {isImg ? <ImgIcon size={16} style={{ color: "var(--lime)" }} /> : <FileText size={16} style={{ color: "var(--lime)" }} />}
                <span className="truncate text-xs" title={f.original_filename}>{f.original_filename}</span>
                <button
                  type="button"
                  onClick={() => remove(f.id)}
                  className="ml-auto"
                  style={{ color: "var(--text-muted)" }}
                  data-testid={`remove-file-${f.id}`}
                >
                  <X size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
